// Pulse tab — v2 tile-grid redesign.
//
// Three dimensions (Country / Actor / Family), six tiles each, gradient
// area curves with delta + cross-attribution sub-lines. Clicking a tile
// drills into ExplorePage with the appropriate filter param so the rest
// of the SPA narrows to that entity.
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { fetchPulse } from '../api';
import DimensionToggle, { useDimension } from '../components/DimensionToggle';
import RangeChips, { usePulseRange } from '../components/RangeChips';
import PulseGrid from '../components/PulseGrid';
import type { PulseTile } from '../types';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from './_shared';

export default function PulseTab() {
  const range = usePulseRange();
  const dimension = useDimension();
  const navigate = useNavigate();

  const q = useQuery({
    queryKey: ['trends', 'pulse', 'v2', range, dimension],
    queryFn: () => fetchPulse(range, dimension),
    ...QUERY_OPTIONS,
  });

  const handleTileSelect = useCallback(
    (tile: PulseTile) => {
      // Wire-through to ExplorePage filter params. ExplorePage already
      // supports `?country=`; `actor=` / `family=` are siblings added in
      // this same change. We use the entity id directly because that's
      // what the explore filter accepts (validated server-side).
      const params = new URLSearchParams();
      if (tile.type === 'country') {
        if (tile.id === '∅') return; // unattributed bucket is not filterable
        params.set('country', tile.id);
      } else if (tile.type === 'actor') {
        params.set('actor', tile.name);
      } else if (tile.type === 'family') {
        params.set('family', tile.name);
      }
      navigate(`/explore?${params.toString()}`);
    },
    [navigate],
  );

  const tiles = q.data?.tiles ?? [];

  return (
    <section className="trends-tab-content trends-pulse">
      <header className="pulse-header">
        <div className="pulse-header-controls">
          <DimensionToggle />
          <RangeChips />
        </div>
        {q.data?.anchor_period && (
          <p className="pulse-header-context">
            Showing <strong>{tiles.length}</strong> top{' '}
            {dimension === 'country' ? 'countries' : dimension === 'actor' ? 'actors' : 'families'}{' '}
            · anchor {q.data.anchor_period} · baseline{' '}
            {q.data.baseline_period ?? '—'} · {q.data.bucket ?? 'monthly'} buckets
          </p>
        )}
      </header>

      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="The trends snapshot hasn't produced Pulse data yet. Once the Dagster trends_snapshot asset runs (and the SCHEMA_VERSION=2 rebuild lands), this populates automatically."
      >
        {q.data && (
          <>
            <BuiltAt at={q.data.built_at} />
            <PulseGrid tiles={tiles} onSelect={handleTileSelect} />
          </>
        )}
      </TabStatus>
    </section>
  );
}
